import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { Router } from "@angular/router";
import { SocketClusterService } from "@core/services/socket-cluster.service";
import { IPlatformToolHandler } from "@shared/components/platform-tool/platform-tool-container/platform-tool-component";
import { MagicLinkModel } from "@shared/models/platform-tool-models";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {
  AdditionalCheckListItem,
  CheckStatus,
  ChecklistItem,
  JumpRunProgress,
  ScenarioCalculationStepModel,
} from "./step-by-step-process";
import {
  StepByStepProcessContext,
  StepByStepProcessHandler,
} from "./step-by-step-process.handler";

@Component({
  standalone: false,
  selector: "app-step-by-step-process",
  templateUrl: "./step-by-step-process.component.html",
  styleUrls: ["./step-by-step-process.component.scss"],
})
export class StepByStepProcessComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() scenarioCalculationSteps: ScenarioCalculationStepModel[];
  @Input() platformLink: MagicLinkModel;
  @Input() handler: IPlatformToolHandler;
  @Input() isAutomaticNavigation: boolean = true;
  @Input() disableChecklist: boolean = false;
  @Input() isStepJumpEnabled: boolean = false;
  @Input() shouldCheckWorkflowStatus: boolean = false;

  private unsubscribe$ = new Subject<void>();
  sbsChannelSubscriber$ = new Subject<void>();

  jumpRun: JumpRunProgress | null = null;

  workflowMessage: string = "Please complete the associated ";
  mandatoryWorkflowQuestion: string = `<div class="note">
            <strong style="display: block;font-size: 16px;margin-block-end: 20px">Do you want to run all mandatory workflow in this step?</strong>
            <p>By continuing, the system will initiate the related mandatory workflows.</p>
            </div>`;

  private readonly ctx: StepByStepProcessContext = (() => {
    const self = this;
    return {
      get platformLink() {
        return self.platformLink;
      },
      get isAutomaticNavigation() {
        return self.isAutomaticNavigation;
      },
      get toolHandler() {
        return self.handler;
      },
      get steps() {
        return self.scenarioCalculationSteps;
      },
    };
  })();

  constructor(
    private stepHandler: StepByStepProcessHandler,
    private socketClusterService: SocketClusterService,
    private router: Router,
  ) {}

  ngAfterViewInit(): void {
    this.focusPanel();
  }

  ngOnInit(): void {
    // jumpRun payloads must reach this component regardless of shouldCheckWorkflowStatus, which only gates the existing disable.
    const scenarioId = this.scenarioCalculationSteps[0].scenarioId;
    const scenarioType = this.scenarioCalculationSteps[0].scenarioType;
    const channel = "sbs-" + scenarioId + "-" + scenarioType;
    this.socketClusterService
      .subscribeToChannel(channel)
      .pipe(takeUntil(this.sbsChannelSubscriber$))
      .subscribe((notification) => {
        console.log("SbS notification notification", notification);
        const payload = notification.payload ?? {};
        if (payload.jumpRun) {
          this.jumpRun = payload.jumpRun;
          if (payload.disableCheckList !== undefined) {
            this.disableChecklist = payload.disableCheckList;
          }
          if (
            payload.jumpRun.status === "Running" &&
            payload.jumpRun.navigateUrl
          ) {
            // Enter this segment's own workflow view, exactly as a real click would.
            this.router.navigateByUrl(payload.jumpRun.navigateUrl);
          }
          if (
            payload.jumpRun.status === "Completed" ||
            payload.jumpRun.status === "Failed"
          ) {
            const finishedJumpRun = payload.jumpRun;
            this.jumpRun = null;
            if (finishedJumpRun.navigateUrl) {
              // Return the browser to the calculator it started from.
              this.router.navigateByUrl(finishedJumpRun.navigateUrl);
            }
            this.stepHandler.onJumpRunFinished(finishedJumpRun, this.ctx);
          }
        } else if (
          this.shouldCheckWorkflowStatus &&
          payload.disableCheckList !== undefined
        ) {
          this.disableChecklist = payload.disableCheckList;
        }
      });

    this.stepHandler.stepsRefreshed$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(({ steps, disableChecklist, isStepJumpEnabled, shouldFocus }) => {
        this.scenarioCalculationSteps = steps;
        this.disableChecklist = disableChecklist;
        this.isStepJumpEnabled = isStepJumpEnabled;
        if (shouldFocus) {
          setTimeout(() => this.focusPanel(), 0);
        }
      });

    this.stepHandler.focusRequested$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => setTimeout(() => this.focusPanel(), 0));

    this.stepHandler.jumpRunResumed$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((jumpRun) => {
        this.jumpRun = jumpRun;
      });

    // Catches this remounted instance up on a jump missed while unsubscribed above; a no-op otherwise.
    this.stepHandler.reconcileJumpRunOnMount(this.ctx);
  }

  focusPanel(): void {
    const activeScenarioCalculationStep = this.scenarioCalculationSteps.find(
      (step) => step.status === CheckStatus.InProgress,
    );

    if (activeScenarioCalculationStep?.id) {
      const element = document.getElementById(activeScenarioCalculationStep.id);
      if (element) {
        element.scrollIntoView(true);
      } else {
        console.log("====> DOM ELEMENT NOT FOUND!");
      }
    }
  }

  onActivateCalculator = (
    scenarioCalculationStep: ScenarioCalculationStepModel,
  ) => {
    this.stepHandler.onActivateCalculator(scenarioCalculationStep, this.ctx);
  };

  onMarkAsComplete = (
    scenarioCalculationStep: ScenarioCalculationStepModel,
    checklistIndex?: number,
  ) => {
    this.stepHandler.onMarkAsComplete(
      scenarioCalculationStep,
      this.ctx,
      checklistIndex,
    );
  };

  onExecuteOtherWorkflow = (
    scenarioCalculationStep: ScenarioCalculationStepModel,
    checklistIndex: number,
    additionalCheckListItem: AdditionalCheckListItem = null,
  ) => {
    this.stepHandler.onExecuteOtherWorkflow(
      scenarioCalculationStep,
      checklistIndex,
      additionalCheckListItem,
      this.ctx,
    );
  };

  onHyperlinkClick = (
    scenarioCalculationStep: ScenarioCalculationStepModel,
    checklist: ChecklistItem,
    checklistIndex: number,
  ) => {
    this.stepHandler.onHyperlinkClick(
      scenarioCalculationStep,
      checklist,
      checklistIndex,
      this.ctx,
    );
  };

  onDescriptionHyperlinkClick(event: MouseEvent, checklist: ChecklistItem) {
    this.stepHandler.onDescriptionHyperlinkClick(event, checklist);
  }

  IsStepCheckboxDisabled(step: ScenarioCalculationStepModel): boolean {
    if (step.isCheckboxDisabled || this.disableChecklist) return true;
    if (step.status === CheckStatus.Pending) return !this.canJumpTo(step);
    return false;
  }

  private canJumpTo(step: ScenarioCalculationStepModel): boolean {
    if (!this.isStepJumpEnabled) return false;
    const current = this.scenarioCalculationSteps.find(
      (s) => s.status === CheckStatus.InProgress,
    );
    return !!current && step.step > current.step;
  }

  // Only whole-step jumps are jump-eligible; a checklist item's own checkbox is never a jump gesture.
  isChecklistCheckboxDisabled(
    step: ScenarioCalculationStepModel,
    checklist: ChecklistItem,
  ): boolean {
    return this.disableChecklist || !checklist.isCheckboxEnabled;
  }

  onChecklistCheckboxChange = (
    checked: boolean,
    scenarioCalculationStep: ScenarioCalculationStepModel,
    checklist: ChecklistItem,
  ) => {
    this.onCheck(checked, scenarioCalculationStep, checklist);
  };

  onAllChecking(
    $event: any,
    scenarioCalculationStep: ScenarioCalculationStepModel,
  ) {
    this.stepHandler.onAllChecking($event, scenarioCalculationStep, this.ctx);
  }

  onCheck = (
    checked: any,
    scenarioCalculationStep: ScenarioCalculationStepModel,
    checklist: ChecklistItem,
  ) => {
    this.stepHandler.onCheck(
      checked,
      scenarioCalculationStep,
      checklist,
      this.ctx,
    );
  };

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.sbsChannelSubscriber$.complete();
  }

  getChecklistFormatedText(
    stepIndex: number,
    text: string,
    index: number,
  ): string {
    index++;
    stepIndex++;
    return stepIndex + "." + index + ". " + text;
  }
}
